import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useIsConnectionRestored, useTonWallet } from '@tonconnect/ui-react';
import { ConnectScreen } from './screens/ConnectScreen';
import { TabBar } from './components/TabBar';
import { ToastHost } from './components/ToastHost';
import { isShotHarness } from './lib/shot';
import { isDevSessionBypass } from './lib/session';
import { StoreProvider } from './store/store';

// Route screens are code-split: each loads on demand so the initial bundle
// stays lean (the EscrowVault orb lands with the Deal screen only).
const BrowseScreen = lazy(() => import('./screens/BrowseScreen').then((m) => ({ default: m.BrowseScreen })));
const JobScreen = lazy(() => import('./screens/JobScreen').then((m) => ({ default: m.JobScreen })));
const DealsScreen = lazy(() => import('./screens/DealsScreen').then((m) => ({ default: m.DealsScreen })));
const DealScreen = lazy(() => import('./screens/DealScreen').then((m) => ({ default: m.DealScreen })));
const PostJobScreen = lazy(() => import('./screens/PostJobScreen').then((m) => ({ default: m.PostJobScreen })));
const ProfileScreen = lazy(() => import('./screens/ProfileScreen').then((m) => ({ default: m.ProfileScreen })));

// Design review surface — every component under worst-case data. Deliberately
// sits above the connect gate and outside StoreProvider so the shooter can load
// it cold, and so it never depends on ledger state.
const GalleryScreen = lazy(() => import('./gallery/GalleryScreen').then((m) => ({ default: m.GalleryScreen })));

function ScreenFallback() {
  return (
    <div
      className="min-h-[60dvh] flex items-center justify-center font-mono text-micro font-medium uppercase tracking-label text-fg-3"
      aria-busy="true"
    >
      Loading the ledger.
    </div>
  );
}

function App() {
  const { pathname } = useLocation();
  const wallet = useTonWallet();
  const restored = useIsConnectionRestored();
  const shotBypass = isShotHarness();
  const gateBypass = shotBypass || isDevSessionBypass();

  if (pathname.startsWith('/gallery')) {
    return (
      <Suspense fallback={<ScreenFallback />}>
        <GalleryScreen />
      </Suspense>
    );
  }

  if (!gateBypass && !restored) {
    return (
      <div className="screen">
        <ScreenFallback />
      </div>
    );
  }

  if (!gateBypass && !wallet) {
    return (
      <div className="screen">
        <ConnectScreen />
      </div>
    );
  }

  return (
    <StoreProvider>
      <div className="screen pb-[72px]">
        <Suspense fallback={<ScreenFallback />}>
          <Routes>
            <Route path="/" element={<BrowseScreen />} />
            <Route path="/jobs/:id" element={<JobScreen />} />
            <Route path="/deals" element={<DealsScreen />} />
            <Route path="/deals/:id" element={<DealScreen />} />
            <Route path="/post" element={<PostJobScreen />} />
            <Route path="/profile" element={<ProfileScreen />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
        <ToastHost />
        <TabBar />
      </div>
    </StoreProvider>
  );
}

export default App;
